import { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_APP_EMBED_STATUS } from 'gql/queries';
import { StoreValues } from 'types/store';
import { readThemeAppEmbedEvidence, Status } from 'utils/themeAppEmbed';
import type { InstallationStatus } from 'types/installationVerification';
import {
  configuredInstallationBoolean, currentInstallationScope, currentThemeInstallationMethod,
  hasCurrentInstallationConfiguration, readCurrentInstallationConfiguration,
  readCurrentInstallationStatus,
} from 'utils/currentInstallationState';

type Evidence = {
  scope: string | null;
  installationMethod: 'theme' | null;
  checking: boolean;
  companion: boolean;
  addToCart: boolean | null;
  checkoutPlus: boolean | null;
  activationStatus: Status;
  installationStatus?: InstallationStatus;
  installationStatusUnavailable?: boolean;
};

export interface CurrentInstallationState {
  usesCompanion: boolean;
  installationMethod: 'theme' | null;
  addToCart: boolean | null;
  checkoutPlus: boolean | null;
  checking: boolean;
  canRefresh: boolean;
  refresh: () => void;
  activationStatus?: Status;
  installationStatus?: InstallationStatus;
  installationStatusUnavailable?: boolean;
}

/** Read-only facts; neither enabled flags nor draft intent identify a layout. */
export const useCurrentInstallationState = (storeId: string, store: StoreValues): CurrentInstallationState => {
  const scope = currentInstallationScope(storeId, store);
  const domain = scope ? store.store.toLowerCase() : undefined;
  const [query] = useLazyQuery(GET_APP_EMBED_STATUS, {
    fetchPolicy: 'no-cache', context: { queryDeduplication: false },
  });
  const [evidence, setEvidence] = useState<Evidence>({ scope: null, installationMethod: null,
    checking: false, companion: false, addToCart: null, checkoutPlus: null, activationStatus: 'unavailable' });
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const generation = useRef(0);
  const mounted = useRef(false);
  const companionScope = useRef<string | null>(null);

  const refresh = useCallback(() => {
    const request = ++generation.current;
    if (companionScope.current !== scope) companionScope.current = null;
    setEvidence({ scope, installationMethod: null, checking: Boolean(scope),
      companion: Boolean(scope) && companionScope.current === scope, addToCart: null, checkoutPlus: null,
      activationStatus: scope ? 'checking' : 'unavailable' });
    if (!scope || !domain) return;
    const read = async () => {
      let installationMethod: 'theme' | null = null;
      let companion = false;
      let activationStatus: Status = 'unavailable';
      let installationStatus: InstallationStatus | undefined;
      let installationStatusUnavailable = true;
      let facts: ReturnType<typeof readCurrentInstallationConfiguration> = null;
      try {
        const result = await query({ variables: { storeId } });
        const errors = (result as { errors?: unknown[] }).errors;
        const payload = result.data?.getAppEmbedStatus;
        companion = hasCurrentInstallationConfiguration(payload);
        if (!result.error && !errors?.length) {
          activationStatus = readThemeAppEmbedEvidence(payload, storeId, domain).status;
          const actual = readCurrentInstallationStatus(payload, storeId, domain);
          installationStatus = actual.status;
          installationStatusUnavailable = actual.unavailable || (!actual.status && activationStatus === 'unavailable');
          activationStatus = actual.unavailable ? 'unavailable' : actual.status?.activation.status ?? activationStatus;
          if (companion) facts = readCurrentInstallationConfiguration(payload, storeId, domain);
          else installationMethod = currentThemeInstallationMethod(payload, storeId, domain);
        }
      } catch {}
      if (mounted.current && request === generation.current && currentScope.current === scope) {
        if (companion) companionScope.current = scope;
        const usesCompanion = companionScope.current === scope;
        setEvidence({ scope, installationMethod: usesCompanion ? facts?.installationMethod ?? null : installationMethod,
          checking: false, companion: usesCompanion,
          addToCart: facts?.addToCart ?? null, checkoutPlus: facts?.checkoutPlus ?? null, activationStatus,
          installationStatus, installationStatusUnavailable });
      }
    };
    void read();
  }, [domain, query, scope, storeId]);

  const invalidate = useCallback(() => { mounted.current = false; generation.current++; }, []);

  useEffect(() => {
    mounted.current = true;
    refresh();
    return invalidate;
  }, [invalidate, refresh]);

  const current = Boolean(scope) && evidence.scope === scope;
  return {
    usesCompanion: current && evidence.companion,
    installationMethod: current ? evidence.installationMethod : null,
    addToCart: scope ? current && evidence.companion ? evidence.addToCart
      : configuredInstallationBoolean(store.addToCartPlus) : null,
    checkoutPlus: scope ? current && evidence.companion ? evidence.checkoutPlus
      : configuredInstallationBoolean(store.checkoutPlus) : null,
    checking: Boolean(scope) && (!current || evidence.checking),
    activationStatus: scope ? current ? evidence.activationStatus : 'checking' : 'unavailable',
    installationStatus: current && !evidence.checking ? evidence.installationStatus : undefined,
    installationStatusUnavailable: current && !evidence.checking ? evidence.installationStatusUnavailable === true : !scope,
    canRefresh: Boolean(scope), refresh,
  };
};
